import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, ArrowRight } from "lucide-react";
import Logo from "@/components/Logo";
import MarqueeBanner from "@/components/MarqueeBanner";

const navLinks = [
  { to: "/", label: "Home" },
  { to: "/about", label: "About" },
  { to: "/problem", label: "The Problem" },
  { to: "/solution", label: "The Solution" },
  { to: "/platform", label: "Platform" },
  { to: "/impact", label: "Impact" },
  { to: "/community", label: "Community" },
  { to: "/partners", label: "Partners" },
  { to: "/founder", label: "Founder" },
];

export default function Layout({ children }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  const isV2 = location.pathname.startsWith("/v2");

  // Version 2 (simplified comparison site) renders with its own chrome — V1 untouched.
  if (isV2) {
    return (
      <div className="min-h-screen bg-cream flex flex-col" data-testid="app-root">
        <main className="flex-1" data-testid="page-main">
          {children}
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream flex flex-col" data-testid="app-root">
      <header
        data-testid="site-header"
        className={`sticky top-0 z-50 transition-colors duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border-b border-borderSubtle"
            : "bg-white/60 backdrop-blur-md border-b border-transparent"
        }`}
      >
        <MarqueeBanner />
        <div className="max-w-[1240px] mx-auto px-4 lg:px-6 h-[130px] lg:h-[160px] flex items-center justify-between">
          <Link to="/" data-testid="nav-logo-link" className="flex items-center gap-3 flex-shrink-0">
            <Logo />
          </Link>

          <nav className="hidden xl:flex items-center gap-4 2xl:gap-5" data-testid="desktop-nav">
            {navLinks.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                end={l.to === "/"}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm xl:text-base font-semibold tracking-tight transition-colors link-underline whitespace-nowrap ${
                    isActive ? "text-navy-800" : "text-slateSoft hover:text-navy-800"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3 lg:ml-4">
            <Link
              to="/contact"
              data-testid="nav-request-demo-btn"
              className="hidden sm:inline-flex items-center gap-2 whitespace-nowrap bg-gold hover:bg-gold-deep text-navy text-sm font-semibold px-5 py-2.5 rounded-md transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold focus-visible:ring-offset-2"
            >
              Request a Demo <ArrowRight className="w-4 h-4" strokeWidth={2.5} />
            </Link>
            <button
              onClick={() => setOpen(!open)}
              data-testid="mobile-menu-toggle"
              className="xl:hidden p-2 text-navy-800"
              aria-label="Toggle menu"
            >
              {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {open && (
          <div className="xl:hidden border-t border-borderSubtle bg-white" data-testid="mobile-nav">
            <div className="px-6 py-4 flex flex-col gap-1">
              {navLinks.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  end={l.to === "/"}
                  data-testid={`mobile-nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `py-3 px-2 text-base font-medium ${isActive ? "text-navy-800" : "text-slateSoft"}`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <Link
                to="/contact"
                data-testid="mobile-nav-contact-link"
                className="py-3 px-2 text-base font-semibold text-gold"
              >
                Contact / Request a Demo
              </Link>
            </div>
          </div>
        )}
      </header>

      <main className="flex-1" data-testid="page-main">
        {children}
      </main>

      <Footer />
    </div>
  );
}

function Footer() {
  return (
    <footer className="bg-navy-900 text-white mt-24 grain" data-testid="site-footer">
      <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <Logo variant="light" />
          <p className="mt-5 text-navy-100 max-w-md text-[15px] leading-relaxed">
            <span className="text-cream font-semibold">WHO is a coordination infrastructure company.</span>
            {" "}Every warm handoff is coordinated by a WHO Guide&trade; &mdash; a real person supported
            by AI &mdash; so a referral becomes an accountable, measurable connection between the person,
            the organizations, and the outcome.
          </p>
          <p className="mt-5 eyebrow text-white/70">If not you, then WHO.</p>
        </div>

        <div>
          <p className="eyebrow text-navy-200 mb-4">Explore</p>
          <ul className="space-y-2.5 text-[15px]">
            {navLinks.map((l) => (
              <li key={l.to}>
                <Link to={l.to} className="text-white/85 hover:text-gold transition-colors" data-testid={`footer-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}>
                  {l.label}
                </Link>
              </li>
            ))}
            <li>
              <Link to="/contact" className="text-white/85 hover:text-gold transition-colors" data-testid="footer-link-contact">
                Contact
              </Link>
            </li>
            <li>
              <Link to="/sources" className="text-white/85 hover:text-gold transition-colors" data-testid="footer-link-sources">
                Facts &amp; Sources
              </Link>
            </li>
          </ul>
        </div>

        <div>
          <p className="eyebrow text-navy-200 mb-4">Connect</p>
          <ul className="space-y-2.5 text-[15px] text-white/85">
            <li data-testid="footer-email">
              <a href="mailto:marlon@warmhandoffhq.com" className="hover:text-gold transition-colors">
                marlon@warmhandoffhq.com
              </a>
            </li>
            <li>
              <Link to="/privacy" className="hover:text-gold transition-colors" data-testid="footer-link-privacy">
                Privacy Policy
              </Link>
            </li>
            <li>Pittsburgh, Pennsylvania</li>
            <li className="pt-1">
              <a
                href="https://www.linkedin.com/company/who-warm-hand-off"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="footer-linkedin"
                className="inline-flex items-center gap-2 text-white/85 hover:text-gold transition-colors"
                aria-label="WHO on LinkedIn"
              >
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4" aria-hidden>
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.024-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.777 13.019H3.556V9h3.558v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
                LinkedIn
              </a>
            </li>
            <li className="pt-3">
              <Link
                to="/contact"
                data-testid="footer-join-network-btn"
                className="inline-flex items-center gap-2 border border-white/25 hover:border-gold hover:text-gold text-white px-4 py-2 rounded-md text-sm font-semibold transition-colors"
              >
                Join the Network <ArrowRight className="w-4 h-4" />
              </Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-white/10">
        <div className="max-w-[1240px] mx-auto px-6 lg:px-10 py-6 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-xs text-white/60">
          <p>© 2026 WHO — Warm Hand Off LLC. All rights reserved.</p>
          <p className="font-semibold text-gold-light/90 tracking-wide" data-testid="footer-signature">Born in Pittsburgh. Built for Veterans. Designed for National Impact.</p>
          <p>Warm Hand Off · Coordinating people, organizations, and outcomes.</p>
        </div>
      </div>
    </footer>
  );
}
